import React, { useState, useEffect, useRef } from "react";
import { Activity, Mic, Brain, Stethoscope, AlertTriangle, HeartPulse, Thermometer, Send } from "lucide-react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import Logo from "../assets/logo.png";
import ThemeToggle from "../components/ThemeToggle";
import "./SymptomChat.css";
import api from "../lib/api";

const mockUserRecord = {
  name: "John Doe",
  age: 28,
  gender: "Male",
  weight: "70kg",
  height: "175cm",
  bloodType: "O+",
  existingConditions: ["Asthma"],
  medications: ["Ventolin Inhaler"],
  allergies: ["Penicillin"],
  lifestyle: { smoker: false, alcohol: "Occasional", exercise: "3 times per week", diet: "Balanced" },
  recentIllnesses: ["Common cold (last month)"],
  familyHistory: ["Hypertension", "Diabetes"],
  lastCheckup: "2025-04-10",
};

const ChatPage = () => {
  const [chatMessages, setChatMessages] = useState([]);
  const [typedMessage, setTypedMessage] = useState("");
  const chatEndRef = useRef(null);

  // Scroll to latest message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  useEffect(() => {
    const latest = [...chatMessages].reverse().find((msg) => {
      const sender = (msg?.sender || "").toLowerCase();
      const isAssistant = sender === "ai" || sender === "asha";
      return isAssistant && (msg?.text || "").trim().length > 0;
    });
    if (!latest) return;

    try {
      localStorage.setItem(
        "qureo_ai_last_message",
        JSON.stringify({
          text: latest.text,
          sender: latest.sender === "ai" ? "asha" : latest.sender,
          time: new Date().toISOString(),
        })
      );
    } catch {
      // Ignore storage failures silently.
    }
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!typedMessage.trim()) return;

    const userMsg = { sender: "user", text: typedMessage };
    setChatMessages((prev) => [...prev, userMsg]);
    setTypedMessage("");

    // Log activity
    try {
      await api.post('/api/activities/add-activity', { message: 'Chatted with AI health assistant' });
    } catch (err) {
      console.error('Failed to log AI chat activity', err);
    }

    try {
      const res = await fetch("https://backend-qureo-m7nd.onrender.com/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: typedMessage,
          context: chatMessages,
          userRecord: mockUserRecord,
        }),
      });

      const data = await res.json();
      const aiMsg = { sender: "ai", text: data.message, severity: data.severity };
      setChatMessages((prev) => [...prev, aiMsg]);
    } catch {
      setChatMessages((prev) => [
        ...prev,
        { sender: "ai", text: "AI connection error. Please retry." },
      ]);
    }
  };

  return (
    <div className="wizard-container full">
      <div className="chat-page">
        <div className="chat-header-bar">
          <div className="header-left">
            <img src={Logo} alt="Logo" className="chat-logo" />
            <h3>Qureo AI Assistant</h3>
          </div>
          <ThemeToggle />
        </div>

        <div className="chat-body">
          {chatMessages.map((msg, idx) => (
            <div key={idx} className={`chat-bubble ${msg.sender}`}>
              <div className="chat-text">{msg.text}</div>
              {msg.severity && (
                <div className={`severity-tag ${msg.severity.toLowerCase()}`}>
                  Severity: {msg.severity}
                </div>
              )}
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        <div className="chat-input-box">
          <input
            className="chat-input"
            placeholder="Ask a follow-up question..."
            value={typedMessage}
            onChange={(e) => setTypedMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
          />
          <button className="send-btn" onClick={handleSendMessage}>
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
